import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { ClientFeaturesResource } from '@api';
import { ClientResourceService } from '../resources/client-resource.service';
import { InitialLoadingService } from '../services/initial-loading.service';
/**
 * Interface for configuring the feature flag guard data in route definitions.
 *r
 * @interface ClientSubscriptionFeatureGuardData
 * @property {keyof ClientFeaturesResource} clientSubscriptionFeatureFlag - Feature flag that must be enabled for route access
 */
export interface ClientSubscriptionFeatureGuardData {
  clientSubscriptionFeatureFlag: keyof ClientFeaturesResource;
}

/**
 * Angular route guard that controls access to routes based on client subscription feature flags.
 *
 * This guard checks if all required feature flags are enabled using GrowthBook service.
 * If any required flag is disabled, the user is redirected to a missing feature page.
 *
 * @example
 * // Basic usage in route configuration
 * {
 *   path: 'monetization',
 *   component: MonetizationComponent,
 *   canActivate: [featureFlagGuard],
 *   data: {
 *     clientSubscriptionFeatureFlag: 'isPaidWebinarEnabled',
 *   }
 * }
 *
 *
 * @param route - The activated route snapshot containing guard data
 * @param _state - The router state snapshot (unused)
 * @returns {boolean} - True if access is granted, false if redirected
 */
export const clientSubscriptionFeatureGuard: CanActivateFn = (route, _state) => {
  const router = inject(Router);
  const clientResourceService = inject(ClientResourceService);
  const initialLoadingService = inject(InitialLoadingService);

  return initialLoadingService.waitForInitialization(() => {
    const data = route.data as ClientSubscriptionFeatureGuardData;
    const hasFeatureEnabled =
      clientResourceService.client.value()?.features[data.clientSubscriptionFeatureFlag] === true;

    if (!hasFeatureEnabled) {
      const redirectTo = '/missing-feature';
      router.navigate([redirectTo], {
        queryParams: { missingFeature: data.clientSubscriptionFeatureFlag },
      });
      return false;
    }

    return true;
  });
};
